import React, { useCallback } from 'react';
import { useAgentTeamStore } from '../../store/agentTeamStore';

const PlusIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const HistoryIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

interface AgentTeamHeaderProps {
  showSessionList?: boolean;
  onToggleSessionList?: () => void;
}

export const AgentTeamHeader: React.FC<AgentTeamHeaderProps> = ({
  showSessionList = false,
  onToggleSessionList,
}) => {
  const {
    session,
    loading,
    clearSession,
    loadSession,
  } = useAgentTeamStore();

  const handleNewSession = useCallback(() => {
    if (loading) return;
    clearSession();
  }, [loading, clearSession]);

  const handleRefresh = useCallback(async () => {
    if (loading || !session) return;
    try {
      await loadSession(session.id);
    } catch (error) {
      console.error('刷新会话失败:', error);
    }
  }, [session, loading, loadSession]);

  const sessionTitle = session?.title || '智能体团队';

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">👥</span>
          <span className="text-sm text-gray-900 font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]">
            {sessionTitle}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={handleRefresh}
          disabled={loading || !session}
          className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="刷新"
          aria-label="刷新会话"
        >
          <RefreshIcon />
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <button
          onClick={onToggleSessionList}
          disabled={loading}
          className={`p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            showSessionList ? 'text-gray-900 bg-gray-100' : 'text-gray-700 hover:text-gray-900'
          }`}
          title="会话历史"
          aria-label="会话历史"
          aria-pressed={showSessionList}
        >
          <HistoryIcon />
        </button>

        <button
          onClick={handleNewSession}
          disabled={loading}
          className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="新建会话"
          aria-label="新建会话"
        >
          <PlusIcon />
        </button>
      </div>
    </div>
  );
};

export default AgentTeamHeader;
